import { Component, Input, Output, EventEmitter, TemplateRef, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { DataTableComponent } from '../data-table/data-table.component';
import { DataGridComponent } from '../data-grid/data-grid.component';
import { TableColumn, TableConfig } from '../../models/table.models';

@Component({
    selector: 'app-grid-list-view',
    imports: [TableModule, CommonModule, PaginatorModule, SkeletonModule, DataTableComponent, DataGridComponent],
    standalone: true,
    templateUrl: './grid-list-view.component.html',
    styleUrls: ['./grid-list-view.component.scss']
})
export class GridListViewComponent implements OnInit, OnChanges {
    @Input() items: any[] = [];
    @Input() viewMode: 'grid' | 'list' = 'grid';
    @Input() gridItemTemplate!: TemplateRef<any>;
    @Input() listHeaderTemplate!: TemplateRef<any>;
    @Input() listRowTemplate!: TemplateRef<any>;
    @Input() actionBarTemplate?: TemplateRef<any>;
    @Input() selectedItems: any[] = [];
    @Output() selectedItemsChange = new EventEmitter<any[]>();
    @Input() selectionChangeCallback?: (event: any) => void;
    @Input() dataKey: string = '_id';
    @Input() scrollable: boolean = true;
    @Input() tableStyle: any = { 'min-width': '50rem' };
    @Input() rows: number = 5;
    @Input() rowsPerPageOptions: number[] = [5, 10, 20, 50];

    // Pagination Configuration
    @Input() totalRecords: number = 0;
    @Input() first: number = 0;
    @Input() showPaginator: boolean = true;
    @Output() pageChange = new EventEmitter<any>();
    @Output() rowClick = new EventEmitter<any>();

    // Data Table Configuration
    @Input() tableColumns: TableColumn[] = [];
    @Input() tableConfig: TableConfig = {};
    @Input() useDataTable: boolean = false;
    @Input() customTemplates: { [key: string]: TemplateRef<any> } = {};
    @Input() rowClass: string | ((data: any, index: number) => string) = "";

    // Styling Configuration
    @Input() gridCols: number = 1; // Default (mobile-first, 0px+): base grid columns
    @Input() gridColsSm: number = 2; // sm:grid-cols-2 (≥640px - small devices)
    @Input() gridColsMd: number = 3; // md:grid-cols-3 (≥768px - medium devices/tablets)
    @Input() gridColsLg: number = 3; // lg:grid-cols-3 (≥1024px - large devices/landscape tablets)
    @Input() gridColsXl: number = 4; // xl:grid-cols-4 (≥1280px - extra large devices/desktops)
    @Input() gridCols2xl: number = 4; // 2xl:grid-cols-4 (≥1536px - 2x extra large/large desktops)
    @Input() gridGap: number = 3; // gap-3
    @Input() parentClass: string = '';
    @Input() customGridClass: string = '';
    @Input() customActionBarClass: string = '';
    @Input() maxHeight: string = 'auto'; // Max height for the container
    @Input() itemsLoading: boolean = false; // Loading state for items

    // Internal state to ensure change detection
    internalFirst: number = 0;
    tableConfigWithPagination: TableConfig = {};

    @ViewChild(DataTableComponent) dataTable?: DataTableComponent;

    constructor() { }

    ngOnInit() {
        this.internalFirst = this.first;
        this.tableConfigWithPagination = this.buildTableConfigWithPagination();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['first'] && changes['first'].currentValue !== changes['first'].previousValue) {
            this.internalFirst = this.first;
        }
        if (changes['tableConfig'] || changes['rows']) {
            this.tableConfigWithPagination = this.buildTableConfigWithPagination();
        }
    }

    onSelectionChange(event: any) {
        this.selectedItemsChange.emit(event);
        if (this.selectionChangeCallback) {
            this.selectionChangeCallback(event);
        }
    }

    onPageChange(event: any) {
        this.internalFirst = event.first;
        this.pageChange.emit(event);
    }

    onRowClick(rowData: any) {
        this.rowClick.emit(rowData);
    }

    clearSelection() {
        this.dataTable?.clearSelection?.();
    }

    get gridClasses(): string {
        return `grid auto-rows-min items-stretch grid-cols-${this.gridCols} sm:grid-cols-${this.gridColsSm} md:grid-cols-${this.gridColsMd} lg:grid-cols-${this.gridColsLg} xl:grid-cols-${this.gridColsXl} 2xl:grid-cols-${this.gridCols2xl} grid-container gap-${this.gridGap} w-full overflow-y-auto overflow-x-hidden ${this.customGridClass} `;
    }

    get actionBarClasses(): string {
        return `action-bar ${this.customActionBarClass}`;
    }

    get tableConfigWithHeight(): TableConfig {
        return {
            ...this.tableConfig,
            scrollable: true, // Enable PrimeNG flex scrolling
            scrollHeight: 'flex' // Use flex height for proper container scrolling
        };
    }

    buildTableConfigWithPagination(): TableConfig {
        return {
            ...this.tableConfig,
            scrollable: true,
            scrollHeight: 'flex',
            rows: this.rows
        };
    }

    get tableClasses(): string {
        return this.tableConfig.tableClass || '';
    }

    get shouldShowPaginator(): boolean {
        return this.showPaginator && this.totalRecords > 0;
    }

    /**
     * Generates grid columns configuration for the data-grid component
     * @returns Object with breakpoint-specific column counts
     */
    get gridColumnsConfig(): { base?: number; sm?: number; md?: number; lg?: number; xl?: number; '2xl'?: number } {
        return {
            base: this.gridCols,
            sm: this.gridColsSm,
            md: this.gridColsMd,
            lg: this.gridColsLg,
            xl: this.gridColsXl,
            '2xl': this.gridCols2xl
        };
    }
}
